'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { useState } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import type { ReferenceDetail } from '@eliadmin/core/src/api/references/references-types';

export function ReferenceGallery({ reference }: { reference: ReferenceDetail }) {
  const { t } = useTranslations();

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <CarouselProvider>
        <section className={cn('custom-section', 'py-6 pr-0 sm:py-8 md:py-10 lg:py-12')}>
          <div
            className={cn(
              'custom-container',
              'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
            )}
          >
            <div className="flex w-full items-center justify-between">
              <h2 data-ea-visual-tag="h4" className="text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                {t('text2367')}
              </h2>
              <CarouselControls />
            </div>
            <Carousel
              className={{
                container: 'w-full gap-4 sm:gap-5 lg:gap-6',
                main: 'w-full',
                wrapper: 'w-full',
                slide:
                  'relative h-[162px] w-full max-w-[236px] overflow-hidden rounded-[16px] sm:h-[185px] sm:max-w-[280px] sm:rounded-[20px] md:h-[200px] md:max-w-[320px] lg:h-[300px] lg:max-w-[480px] lg:rounded-[24px] xl:h-[400px] xl:max-w-[584px]',
              }}
              slides={(reference.images ?? []).map((image, index) => (
                <Image
                  onClick={handleOpenGallery(reference.images, index)}
                  key={image.id}
                  src={image.url}
                  alt={resolveImageAlt(image, undefined)}
                  width={0}
                  height={0}
                  className="absolute h-full w-full cursor-pointer object-cover object-center transition-all hover:opacity-90"
                  sizes="(min-width: 768px) 50vw, 80vw"
                />
              ))}
            />
          </div>
        </section>
      </CarouselProvider>
    </>
  );
}
